import {
  currenciesCreateMutation,
  currenciesDestroyMutation,
  currenciesListOptions,
  currenciesListQueryKey,
  currenciesPartialUpdateMutation,
  currenciesRetrieveOptions,
  CurrencyReadable
} from '@crm/api'
import { ConfirmModal } from '@crm/components'
import {
  CatalogActions,
  CatalogAddButton,
  FormModal,
  FormNomenclaturesCurrencies
} from '@crm/features/nomenclatures/components'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { defaultFilters } from '@crm/utils'
import { DayLabel, TableContainer } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App } from 'antd'
import { useTheme } from 'antd-style'
import { CloseSquare, Edit, TickSquare, Trash } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'

import type { MenuProps, TableColumnsType } from 'antd'

const formName = 'currencies'

export const NomenclaturesCurrencies = () => {
  const { t } = useLocales()
  const params = useQueryParams()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { resetPagination } = useResetPagination()
  const theme = useTheme()

  const sorter = useTableSorter()

  const { notification } = App.useApp()

  const [isCreate, setIsCreate] = useState(false)
  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()

  const { data: currencies, isLoading } = useQuery(
    currenciesListOptions({
      query: {
        ...defaultFilters,
        ...params
      }
    })
  )

  const deleteCurrenciesMutation = useMutation({
    ...currenciesDestroyMutation(),
    onSuccess: () => {
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
      invalidateQueries([currenciesListQueryKey()])
      resetPagination()
      setDeleteItem(undefined)
    },
    onError: (e) => showApiErrors(e)
  })

  const onDelete = () => {
    if (deleteItem) {
      deleteCurrenciesMutation.mutate({ path: { id: deleteItem } })
    }
  }

  const onCancelDelete = () => setDeleteItem(undefined)

  const onCloseModal = () => {
    setEditItem(undefined)
    setIsCreate(false)
  }

  const getActionColumnItem: (id: number) => MenuProps['items'] = useCallback(
    (id) => [
      {
        label: t('nomenclature.edit'),
        key: 'edit',
        icon: <Edit size={20} />,
        onClick: () => setEditItem(id)
      },
      { type: 'divider' },
      {
        label: t('nomenclature.delete'),
        key: 'delete',
        icon: <Trash size={20} />,
        danger: true,
        onClick: () => setDeleteItem(id)
      }
    ],
    [t]
  )

  const columns: TableColumnsType<CurrencyReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true
      },
      {
        title: t('nomenclature.symbol'),
        dataIndex: 'symbol'
      },
      {
        title: t('nomenclature.ratio'),
        dataIndex: 'ratio'
      },
      {
        title: t('nomenclature.default'),
        dataIndex: 'is_default',
        align: 'center',
        render: (is_default) =>
          is_default ? (
            <TickSquare color={theme.colorSuccess} />
          ) : (
            <CloseSquare color={theme.colorError} />
          )
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        fixed: 'right',
        render: ({ id }) => <CatalogActions options={getActionColumnItem(id)} />
      }
    ],
    [t, getActionColumnItem, theme]
  )

  return (
    <>
      <TableContainer
        data={currencies}
        columns={columns}
        loading={isLoading}
        rightChildren={
          <CatalogAddButton onClick={() => setIsCreate(true)}>
            {t('nomenclature.add_currency')}
          </CatalogAddButton>
        }
        onChange={(_, __, result) => sorter(result)}
        bordered
      />

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCancelDelete}
      />

      <FormModal
        id={editItem}
        formName={formName}
        open={!!(editItem || isCreate)}
        getItem={currenciesRetrieveOptions}
        onCreate={currenciesCreateMutation}
        onUpdate={currenciesPartialUpdateMutation}
        onCloseModal={onCloseModal}
      >
        <FormNomenclaturesCurrencies />
      </FormModal>
    </>
  )
}
